/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { useState, useEffect, useRef } from 'react'
import error from 'next/error'
import { useRouter } from 'next/navigation'
import { ROUTES } from '@utils/api/routes'
import { getUserFromCookies, setUserInCookies } from '@utils/auth'
import { QueryCheckServiceability } from 'src/models/query.models'
import { useUserState } from 'src/providers/login-state-management'
import { useMutationQuery } from '../mutation-query'

interface Location {
  latitude: number | null
  longitude: number | null
}

type ResponseType = {
  location: Location
  error: typeof error
  isLoading: boolean
}

type DetectedCity = {
  cityId?: string
  cityName?: string
}

function isDetectedCity(city: DetectedCity) {
  return Boolean(city.cityId && city.cityName)
}

async function mapPublicIp(provider: string): Promise<DetectedCity> {
  try {
    const ipResponse = await fetch(provider)
    if (!ipResponse.ok) return {}
    const ipData = (await ipResponse.json()) as { ip?: string }
    if (!ipData.ip) return {}

    const mappedResponse = await fetch(
      `/api/location?ip=${encodeURIComponent(ipData.ip)}`
    )
    if (!mappedResponse.ok) return {}
    return await (mappedResponse.json() as Promise<DetectedCity>)
  } catch {
    return {}
  }
}

async function mapProviderCity(provider: string): Promise<DetectedCity> {
  try {
    const response = await fetch(provider)
    if (!response.ok) return {}
    const location = (await response.json()) as {
      city?: string
      cityName?: string
      error?: boolean
      success?: boolean
    }
    const detectedCity = location.city || location.cityName
    if (location.error || location.success === false || !detectedCity) {
      return {}
    }

    const mappedResponse = await fetch(
      `/api/location?city=${encodeURIComponent(detectedCity)}`
    )
    if (!mappedResponse.ok) return {}
    return await (mappedResponse.json() as Promise<DetectedCity>)
  } catch {
    return {}
  }
}

async function getIpDetectedCity(): Promise<DetectedCity> {
  try {
    const serverResponse = await fetch('/api/location')
    if (serverResponse.ok) {
      const serverCity = (await serverResponse.json()) as DetectedCity
      if (isDetectedCity(serverCity)) return serverCity
    }
  } catch {
    // Continue with browser-visible IP detection.
  }

  const ipCities = await Promise.all(
    [
      'https://api64.ipify.org?format=json',
      'https://api.ipify.org?format=json',
    ].map(mapPublicIp)
  )
  const ipCity = ipCities.find(isDetectedCity)
  if (ipCity) return ipCity

  const providerCities = await Promise.all(
    ['https://ipwho.is/', 'https://free.freeipapi.com/api/v1/json/'].map(
      mapProviderCity
    )
  )
  return providerCities.find(isDetectedCity) || {}
}

export const useGeolocation = (): ResponseType => {
  const [location, setLocation] = useState<Location>({
    latitude: null,
    longitude: null,
  })
  const router = useRouter()
  const { addUserDetails, userDetails } = useUserState()
  const userDetailsRef = useRef(userDetails)
  const ipLookupStartedRef = useRef(false)
  userDetailsRef.current = userDetails

  const { mutateAsync, isLoading } = useMutationQuery({
    url: ROUTES.CheckServiceability,
    service: 'DATA_CLIENT',
    method: 'post',
    options: {
      onSuccess: (data: QueryCheckServiceability) => {
        getUserFromCookies()
          .then((user) => {
            if (data?.data?.status === 'fail') {
              if (!userDetails?.cityId) addUserDetails(user)
              else addUserDetails(user)
            } else {
              const updatedUser = {
                ...user,
                cityId: data?.data?.cityId,
                cityName: data?.data?.cityName,
              }
              addUserDetails(updatedUser)
              void setUserInCookies(updatedUser).then(() => router.refresh())
            }
          })
          .catch(() => {})
      },
    },
  })

  useEffect(() => {
    const applyIpLocation = () => {
      if (ipLookupStartedRef.current) return
      ipLookupStartedRef.current = true

      void getUserFromCookies().then((savedUser) => {
        if (userDetailsRef.current?.cityId || savedUser?.cityId) {
          if (!userDetailsRef.current?.cityId) addUserDetails(savedUser)
          return
        }

        getIpDetectedCity()
          .then(async (city: { cityId?: string; cityName?: string }) => {
            if (city.cityId && city.cityName) {
              const latestUser = await getUserFromCookies()
              if (userDetailsRef.current?.cityId || latestUser?.cityId) return

              const updatedUser = { ...latestUser, ...city }
              addUserDetails(updatedUser)
              await setUserInCookies(updatedUser)
              router.refresh()
            }
          })
          .catch(() => {})
      })
    }

    void getUserFromCookies().then((savedUser) => {
      if (userDetailsRef.current?.cityId || savedUser?.cityId) {
        if (!userDetailsRef.current?.cityId) addUserDetails(savedUser)
        return
      }

      // Resolve an approximate city immediately. A later, more accurate
      // browser-geolocation result can still replace it when available.
      applyIpLocation()

      if (!navigator.geolocation) {
        return
      }

      const successCallback = (position: GeolocationPosition) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        })
        void mutateAsync({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        })

        // getUserFromCookies()
        //   .then((user) => {
        //     console.log({
        //       ...user,
        //       location: {
        //         latitude: position.coords.latitude,
        //         longitude: position.coords.longitude,
        //       },
        //     })
        //     addUserDetails({
        //       ...user,
        //       location: {
        //         latitude: position.coords.latitude,
        //         longitude: position.coords.longitude,
        //       },
        //     })
        //     router.refresh()
        //   })
        //   .catch(() => {})
      }
      const errorCallback = (_error: GeolocationPositionError) =>
        applyIpLocation()
      navigator.geolocation.getCurrentPosition(successCallback, errorCallback, {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 300000,
      })
    })
  }, [])

  return { location, error, isLoading }
}
